import React, { useState, useEffect } from 'react';
import { TextField } from '@material-ui/core';

import QueryParam from '../../services/QueryParamService';
import { getSystemDate, getPreviousDate } from '../../services/ProfileService';
import { listCharge, processCharge } from '../../services/StockBorrowChargeService';

import Table, {
  columnType,
  tempFilteredRows,
} from '../../components/ClearingTable/Table';
import AutoCompleteCorrespondent from '../../components/AutoComplete/AutoCompleteCorrespondent';
import AutoCompleteAccountNo from '../../components/AutoComplete/AutoCompleteAccountNo';
import AutoCompleteSymbol from '../../components/AutoComplete/AutoCompleteSymbol';
import SelectCusip from '../../components/AutoComplete/SelectCusip';
import Button from '../../components/Buttons/Button';
import Text from '../../components/Typography/Text';
import SelectSystemCode from '../../components/Dropdown/SelectSystemCode';
import {
  notifyInfo,
  notifyError,
  notifySuccess,
  notifyWarning,
} from '../../components/Messages/Notification';
import {
  CustomBodyRenderCurrencyCreditDebit,
  CustomBodyRenderCurrencyLeft,
  CustomBodyRenderAlign,
  CustomBodyRenderQtyDefault,
  CustomBodyRenderPercentageDefault,
} from '../../components/Table/CustomBodyRender';
import { formatPbDate, formatCurrency } from '../../lib/fmt';
import { formatCurrencySymbol, formatPercentage, formatQty } from '../../lib/fmt2';

export default function StockBorrowCharge() {
  const columns = [
    {
      name: 'tradeDate',
      label: 'Date',
      type: columnType.date,
    },
    {
      name: 'accountId',
      label: 'Account ID',
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
    },
    {
      name: 'accountNo',
      label: 'Account No',
    },
    {
      name: 'originalCusip',
      label: 'Original Cusip',
    },
    {
      name: 'symbol',
      label: 'Symbol',
    },
    {
      name: 'symbolDescription',
      label: 'Symbol Description',
    },
    {
      name: 'cusip',
      label: 'Cusip',
    },
    {
      name: 'qty',
      label: 'Qty',
      type: columnType.quantity,
      addFooter: true,
    },
    {
      name: 'rate',
      label: 'Rate',
      options: {
        customBodyRenderLite: (dataIndex) => {
          let value = tempFilteredRows[dataIndex]?.rate || 0;
          return tempFilteredRows[dataIndex]?.showClientRate ? (
            <div align="right" style={{ color: value < 0 && '#f44336' }}>
              {formatPercentage(value)}
            </div>
          ) : (
            <div></div>
          ); // blank if showClientRate is false
        },
      },
    },
    {
      name: 'fee',
      label: 'Fee',
      type: columnType.amount,
      addFooter: true,
    },
    {
      name: 'marketValue',
      label: 'Market Value',
      type: columnType.amount,
      addFooter: true,
    },
    {
      name: 'closingPrice',
      label: 'Closing Price',
      type: columnType.price,
    },
    {
      name: 'tdQty',
      label: 'TD Qty',
      type: columnType.quantity,
    },
    {
      name: 'sdQty',
      label: 'SD Qty',
      type: columnType.quantity,
    },
    {
      name: 'previousRate',
      label: 'Previous Rate',
      type: columnType.percentage,
    },
    {
      name: 'assetType',
      label: 'Asset Type',
      options: {
        display: false,
      },
    },
    {
      name: 'status',
      label: 'Status',
    },
    {
      name: 'rateId',
      label: 'Rate ID',
      options: {
        display: false,
      },
    },
    {
      name: 'showClientRate',
      label: 'Show Client Rate',
      type: columnType.bool,
      options: {
        display: false,
      },
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    serverSide: true,
    selectableRows: 'none',
    responsive: 'standard',
  };
  const [lockTable, setLockTable] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);
  const [pagination, setPagination] = useState({});
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [footerData, setFooterData] = useState({});
  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      fromTradeDate: '',
      toTradeDate: '',
      dateType: '',
      tradeDate: '',
      correspondent: '',
      accountNo: '',
      symbol: '',
      cusip: '',
      status: '',
    })
  );

  const handleChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    setSearchData({
      ...searchData,
      [input.name]: input.value,
    });
  };

  const handleSearch = async (paginationChange) => {
    let valid = true;
    if (!searchData.fromTradeDate) {
      notifyWarning('From Trade Date is required.');
      valid = false;
    }
    if (!searchData.toTradeDate) {
      notifyWarning('To Trade Date is required.');
      valid = false;
    }

    if (!valid) {
      return;
    }
    try {
      setLoading(true);
      if (paginationChange !== true) {
        setLockTable(false);
      }
      if (paginationChange === true) {
        if (lockTable === true) {
          return;
        }
      }
      QueryParam.set(searchData);

      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };

      const data = await listCharge(searchData, paginationCopy);

      setTotalRows(data.summary.totalRows);
      paginationCopy.count = data.summary.totalRows;
      setPagination(paginationCopy);

      setRows(data.chargesList);

      setFooterData({
        fee: formatCurrency(data.summary.fee),
        marketValue: formatCurrency(data.summary.marketValue),
        qty: formatQty(data.summary.qty),
        sdQty: formatQty(data.summary.sdQty),
        tdQty: formatQty(data.summary.tdQty),
      });

      if (paginationChange !== true) {
        notifyInfo(
          data.summary.totalRows > 0
            ? data.summary.totalRows + ' search result(s)'
            : 'No records found.'
        );
      }
    } catch (error) {
      console.error(error);
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async () => {
    try {
      setProcessing(true);
      const report = await processCharge(searchData);
      handleSearch();
      notifySuccess(report.success);
    } catch (error) {
      notifyError(error.message);
    } finally {
      setProcessing(false);
    }
  };

  const getCsvData = async () => {
    if (lockTable === true) {
      return [];
    }
    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: 0,
      rowsPerPage: totalRows,
    };

    const data = await listCharge(searchData, paginationCopy);
    const rowsCopyOutput = data.chargesList.map((data) => {
      delete data.dateType;
      delete data.fromTradeDate;
      delete data.toTradeDate;
      delete data.firstBorrowDate;
      data.rate = data.showClientRate ? formatPercentage(data.rate) : '';

      return data;
    });

    return rowsCopyOutput;
  };

  useEffect(() => {
    async function initSystemDate() {
      let previousDate = await getPreviousDate();
      setSearchData({
        ...searchData,
        fromTradeDate: searchData.fromTradeDate ? searchData.fromTradeDate : previousDate,
        toTradeDate: searchData.toTradeDate ? searchData.toTradeDate : previousDate,
      });
    }

    initSystemDate();
  }, []);

  /*=========================================================================================
  Table component
  ===========================================================================================*/
  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Stock Borrow Charge" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading}
              loading={loading}
              type="search"
              label={loading ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
          {/* <div className="grd-cell-none">
            <Button
              label="Process"
              onClick={() => {
                handleProcess();
              }}
            />
          </div> */}
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <TextField
              name="fromTradeDate"
              fullWidth
              label="From Trade Date"
              type="date"
              value={searchData.fromTradeDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required={true}
              inputProps={{ max: searchData.toTradeDate }}
            />
          </div>
          <div className="grd-cell">
            <TextField
              name="toTradeDate"
              fullWidth
              label="To Trade Date"
              type="date"
              value={searchData.toTradeDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              required={true}
              inputProps={{ min: searchData.fromTradeDate }}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteCorrespondent
              name="correspondent"
              value={searchData.correspondent}
              onChange={handleChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountNo
              name="accountNo"
              value={searchData.accountNo}
              correspondent={searchData.correspondent}
              onChange={handleChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteSymbol
              name="symbol"
              value={searchData.symbol}
              onChange={handleChange}
            />
          </div>
          <div className="grd-cell">
            <SelectCusip
              name="cusip"
              label="Cusip"
              value={searchData.cusip}
              onChange={handleChange}
            />
          </div>
          <div className="grd-cell">
            <SelectSystemCode
              name="status"
              label="Status"
              type="Status"
              subType="Journal"
              value={searchData.status}
              onChange={handleChange}
            ></SelectSystemCode>
          </div>
        </div>
      </div>
      <div className="mt-20">
        <Table
          title={'Stock Borrow Charge'}
          data={rows}
          columns={columns}
          options={options}
          pagination={pagination}
          onSearch={handleSearch}
          setPagination={setPagination}
          getCsvData={getCsvData}
          footerData={footerData}
        />
      </div>
    </form>
  );
}
